// Définition des exports de chaque rapport : données récupérées (toutes les
// lignes, pas seulement la page affichée), colonnes et indicateurs clés.
import {
  salesReportService,
  financialReportService,
  clientReportService,
  appointmentReportService,
  commissionReportService,
  giftcardReportService,
  subscriptionReportService,
} from "../../../services";
import {
  EXPORT_LIMIT,
  describePeriod,
  getExportApiDateParams,
} from "../../../utils/reportExport";
import { translateStatus } from "../../../utils/formatters";

const PAYMENT_METHODS = {
  CASH: "Espèces",
  CARD: "Carte bancaire",
  MOBILE_MONEY: "Mobile Money",
  FEDAPAY: "Fedapay",
  AUTRE: "Autre (carte cadeau / abonnement)",
};

const TRANSACTION_TYPES = {
  appointment: "Rendez-vous",
  subscription: "Abonnement",
  product: "Produit",
  gift_card: "Carte cadeau",
};

const COMMISSION_STATUS = {
  pending: "En attente",
  paid: "Payé",
  cancelled: "Annulé",
};

// Paramètres d'une liste complète, filtrée sur la période du rapport
const listParams = (filters, extra = {}) => ({
  ...getExportApiDateParams(filters),
  ...extra,
  page: 1,
  limit: EXPORT_LIMIT,
});

const rowsOf = (result) => (Array.isArray(result?.data) ? result.data : []);

// --------------------------------------------------------------------------
// Rapport des ventes
// --------------------------------------------------------------------------
export const buildSalesExport = async (filters, activeTab) => {
  const dateParams = getExportApiDateParams(filters);
  const [summaryRes, detailsRes, topRes, hourlyRes] = await Promise.all([
    salesReportService.getSummary(filters),
    salesReportService.getDetails(listParams(filters)),
    salesReportService.getTopSelling({ ...dateParams, limit: EXPORT_LIMIT }),
    salesReportService.getByHour(dateParams),
  ]);
  const summary = summaryRes?.data || {};
  const overview = summary.overview || {};

  return {
    title: "Rapport des ventes",
    fileBaseName: "rapport-ventes",
    filtersInfo: [describePeriod(filters, summary.period)],
    summary: [
      { label: "Chiffre d'affaires", value: overview.totalRevenue, type: "currency" },
      { label: "Nombre de ventes", value: overview.totalSales, type: "number" },
      { label: "Ticket moyen", value: overview.averageTicket, type: "currency" },
      { label: "Ventes en ligne", value: overview.onlineSales, type: "currency" },
      { label: "Ventes en boutique", value: overview.inStoreSales, type: "currency" },
    ],
    csvSection: { top: 1, hourly: 2 }[activeTab] || 0,
    sections: [
      {
        title: "Détails des ventes",
        rows: rowsOf(detailsRes),
        columns: [
          { label: "Code", value: (r) => r.code },
          { label: "Client", value: (r) => r.clientName || "Client anonyme" },
          { label: "Téléphone", value: (r) => r.clientPhone },
          { label: "Montant", value: (r) => r.amount, type: "currency" },
          { label: "Paiement", value: (r) => PAYMENT_METHODS[r.method] || r.method },
          { label: "Statut", value: (r) => translateStatus(r.status, "payment") },
          {
            label: "Services",
            value: (r) =>
              (r.services || [])
                .map((s) => (s.quantity > 1 ? `${s.name} x${s.quantity}` : s.name))
                .join(", "),
          },
          { label: "Date", value: (r) => r.createdAt, type: "datetime" },
        ],
      },
      {
        title: "Top ventes",
        rows: rowsOf(topRes),
        columns: [
          { label: "Service", value: (r) => r.serviceName },
          { label: "Quantité", value: (r) => r.quantity, type: "number" },
          { label: "Chiffre d'affaires", value: (r) => r.revenue, type: "currency" },
        ],
      },
      {
        title: "Ventes par heure",
        rows: rowsOf(hourlyRes),
        columns: [
          { label: "Heure", value: (r) => `${r.hour}h` },
          { label: "Nombre de ventes", value: (r) => r.sales, type: "number" },
          { label: "Chiffre d'affaires", value: (r) => r.revenue, type: "currency" },
        ],
      },
    ],
  };
};

// --------------------------------------------------------------------------
// Rapport financier
// --------------------------------------------------------------------------
export const buildFinancialExport = async (filters) => {
  const [summaryRes, transactionsRes] = await Promise.all([
    financialReportService.getSummary(filters),
    financialReportService.getTransactions(listParams(filters)),
  ]);
  const summary = summaryRes?.data || {};
  const turnover = summary.turnover || {};
  const byMethod = Object.entries(turnover.byMethod || {}).map(
    ([method, amount]) => ({
      method,
      amount,
      percentage: turnover.total ? (amount / turnover.total) * 100 : 0,
    }),
  );

  return {
    title: "Rapport financier",
    fileBaseName: "rapport-financier",
    filtersInfo: [describePeriod(filters, summary.period)],
    summary: [
      { label: "CA total", value: turnover.total, type: "currency" },
      { label: "CA en ligne", value: turnover.online, type: "currency" },
      { label: "CA en boutique", value: turnover.inStore, type: "currency" },
      { label: "Paiements en attente", value: summary.pendingPayments?.amount, type: "currency" },
      { label: "Remboursements", value: summary.refunds?.amount, type: "currency" },
      { label: "Nombre de remboursements", value: summary.refunds?.count, type: "number" },
      { label: "Passifs abonnements", value: summary.liabilities?.subscriptions, type: "currency" },
      { label: "Passifs cartes cadeaux", value: summary.liabilities?.giftCards, type: "currency" },
    ],
    csvSection: 0,
    sections: [
      {
        title: "Transactions",
        rows: rowsOf(transactionsRes),
        columns: [
          { label: "Code", value: (r) => r.code },
          { label: "Client", value: (r) => r.clientName || "Client anonyme" },
          { label: "Montant", value: (r) => r.amount, type: "currency" },
          { label: "Méthode", value: (r) => PAYMENT_METHODS[r.method] || r.method },
          { label: "Statut", value: (r) => translateStatus(r.status, "payment") },
          { label: "Type", value: (r) => TRANSACTION_TYPES[r.type] || r.type },
          { label: "Date", value: (r) => r.createdAt, type: "datetime" },
        ],
      },
      {
        title: "Répartition par mode de paiement",
        rows: byMethod,
        columns: [
          { label: "Mode de paiement", value: (r) => PAYMENT_METHODS[r.method] || r.method },
          { label: "Montant", value: (r) => r.amount, type: "currency" },
          { label: "Part", value: (r) => r.percentage, type: "percent" },
        ],
      },
    ],
  };
};

// --------------------------------------------------------------------------
// Rapport clients
// --------------------------------------------------------------------------
export const buildClientsExport = async (filters) => {
  const result = await clientReportService.getClients({
    ...filters,
    page: 1,
    limit: EXPORT_LIMIT,
  });
  const summary = result?.summary || {};

  const filtersInfo = [];
  if (filters.search) filtersInfo.push(`Recherche : ${filters.search}`);
  filtersInfo.push(
    filters.isActive ? "Clients actifs uniquement" : "Tous les clients (actifs et inactifs)",
  );
  if (filters.hasActiveSubscription) filtersInfo.push("Avec abonnement actif uniquement");
  if (filters.minAppointments) {
    filtersInfo.push(`Nombre minimum de rendez-vous : ${filters.minAppointments}`);
  }

  return {
    title: "Rapport clients",
    fileBaseName: "rapport-clients",
    filtersInfo,
    summary: [
      { label: "Total clients", value: summary.totalClients, type: "number" },
      { label: "Clients actifs", value: summary.activeClients, type: "number" },
      { label: "Nouveaux clients", value: summary.newClientsThisPeriod, type: "number" },
      { label: "Clients avec rendez-vous", value: summary.clientsWithAppointments, type: "number" },
      { label: "Clients avec abonnement", value: summary.clientsWithSubscriptions, type: "number" },
    ],
    csvSection: 0,
    sections: [
      {
        title: "Clients",
        rows: rowsOf(result),
        columns: [
          { label: "Nom", value: (r) => r.nom },
          { label: "Prénom", value: (r) => r.prenom },
          { label: "Email", value: (r) => r.email },
          { label: "Téléphone", value: (r) => r.telephone },
          { label: "Rendez-vous", value: (r) => r.totalAppointments, type: "number" },
          { label: "Total dépensé", value: (r) => r.totalSpent, type: "currency" },
          { label: "Dernier RDV", value: (r) => r.lastAppointment, type: "date" },
          { label: "Abonnement actif", value: (r) => r.hasActiveSubscription, type: "boolean" },
          { label: "Compte actif", value: (r) => r.isActive, type: "boolean" },
        ],
      },
    ],
  };
};

// --------------------------------------------------------------------------
// Rapport des rendez-vous
// --------------------------------------------------------------------------
export const buildAppointmentsExport = async (filters, activeTab) => {
  const [statsRes, listRes, cancelledRes, waitingRes] = await Promise.all([
    appointmentReportService.getStats(filters),
    appointmentReportService.getList(listParams(filters)),
    appointmentReportService.getCancelled(listParams(filters)),
    appointmentReportService.getWaitingList(listParams(filters)),
  ]);
  const stats = statsRes?.data || {};
  const overview = stats.overview || {};
  const cancelledSummary = cancelledRes?.summary || {};
  const byStatus = Object.entries(stats.byStatus || {}).map(([status, data]) => ({
    status,
    ...data,
  }));

  return {
    title: "Rapport des rendez-vous",
    fileBaseName: "rapport-rendez-vous",
    filtersInfo: [describePeriod(filters, stats.period)],
    summary: [
      { label: "Total rendez-vous", value: overview.totalAppointments, type: "number" },
      { label: "CA généré", value: overview.totalRevenue, type: "currency" },
      { label: "Moyenne par jour", value: overview.averagePerDay, type: "number" },
      { label: "Taux de complétion", value: overview.completionRate, type: "percent" },
      { label: "Taux d'annulation", value: overview.cancellationRate, type: "percent" },
      { label: "Taux d'absence", value: overview.noShowRate, type: "percent" },
      { label: "Total annulés", value: cancelledSummary.totalCancelled, type: "number" },
      { label: "Total manqués", value: cancelledSummary.totalNoShow, type: "number" },
    ],
    csvSection: { cancelled: 1, waiting: 2 }[activeTab] || 0,
    sections: [
      {
        title: "Liste des rendez-vous",
        rows: rowsOf(listRes),
        columns: [
          { label: "Code", value: (r) => r.code },
          { label: "Date", value: (r) => r.date, type: "slot" },
          { label: "Client", value: (r) => r.clientName },
          { label: "Services", value: (r) => (r.services || []).map((s) => s.name).join(", ") },
          {
            label: "Employés",
            value: (r) => (r.employees || []).map((e) => e.name).join(", ") || "Non assigné",
          },
          { label: "Montant", value: (r) => r.totalAmount, type: "currency" },
          { label: "Statut", value: (r) => translateStatus(r.status, "appointment") },
        ],
      },
      {
        title: "Rendez-vous annulés",
        rows: rowsOf(cancelledRes),
        columns: [
          { label: "Code", value: (r) => r.code },
          { label: "Date", value: (r) => r.date, type: "slot" },
          { label: "Client", value: (r) => r.clientName },
          { label: "Téléphone", value: (r) => r.clientPhone },
          { label: "Services", value: (r) => (r.serviceNames || []).join(", ") },
          { label: "Employé", value: (r) => r.employeeName },
          { label: "Raison", value: (r) => r.cancellationReason },
          { label: "Statut", value: (r) => (r.status === "CANCELLED" ? "Annulé" : "Manqué") },
        ],
      },
      {
        title: "Liste d'attente",
        rows: rowsOf(waitingRes),
        columns: [
          { label: "Client", value: (r) => r.clientName },
          { label: "Téléphone", value: (r) => r.clientPhone },
          { label: "Services demandés", value: (r) => (r.requestedServices || []).join(", ") },
          { label: "Date préférée", value: (r) => r.preferredDate, type: "date" },
          { label: "Heure", value: (r) => r.preferredTime },
          { label: "Notifié", value: (r) => r.notified, type: "boolean" },
          { label: "Demandé le", value: (r) => r.requestedAt, type: "date" },
        ],
      },
      {
        title: "Répartition par statut",
        rows: byStatus,
        columns: [
          { label: "Statut", value: (r) => translateStatus(r.status, "appointment") },
          { label: "Nombre", value: (r) => r.count, type: "number" },
          { label: "Part", value: (r) => r.percentage, type: "percent" },
        ],
      },
      {
        title: "Top services",
        rows: stats.byService || [],
        columns: [
          { label: "Service", value: (r) => r.serviceName },
          { label: "Rendez-vous", value: (r) => r.count, type: "number" },
          { label: "Chiffre d'affaires", value: (r) => r.revenue, type: "currency" },
          { label: "Taux d'annulation", value: (r) => r.cancellationRate, type: "percent" },
        ],
      },
    ],
  };
};

// --------------------------------------------------------------------------
// Rapport des commissions
// --------------------------------------------------------------------------
export const buildCommissionsExport = async (filters, activeTab) => {
  const [summaryRes, detailsRes] = await Promise.all([
    commissionReportService.getSummary(filters),
    commissionReportService.getDetails(listParams(filters)),
  ]);
  const summary = summaryRes?.data || {};
  const overview = summary.overview || {};

  return {
    title: "Rapport des commissions",
    fileBaseName: "rapport-commissions",
    filtersInfo: [describePeriod(filters, summary.period)],
    summary: [
      { label: "Total commissions", value: overview.totalCommissions, type: "currency" },
      { label: "CA total", value: overview.totalRevenue, type: "currency" },
      { label: "Taux moyen", value: overview.averageCommissionRate, type: "percent" },
      { label: "Commissions en attente", value: overview.pendingCommissions, type: "currency" },
    ],
    csvSection: activeTab === "details" ? 1 : 0,
    sections: [
      {
        title: "Commissions par employé",
        rows: summary.byEmployee || [],
        columns: [
          { label: "Employé", value: (r) => r.employeeName },
          { label: "Rendez-vous", value: (r) => r.appointmentsCount, type: "number" },
          { label: "CA", value: (r) => r.revenue, type: "currency" },
          { label: "Commission", value: (r) => r.commission, type: "currency" },
          { label: "Taux moyen", value: (r) => r.commissionRate, type: "percent" },
        ],
      },
      {
        title: "Détail des commissions",
        rows: rowsOf(detailsRes),
        columns: [
          { label: "Rendez-vous", value: (r) => r.appointmentCode },
          { label: "Date", value: (r) => r.appointmentDate, type: "date" },
          { label: "Employé", value: (r) => r.employeeName },
          { label: "Service", value: (r) => r.serviceName },
          { label: "CA", value: (r) => r.revenue, type: "currency" },
          { label: "Taux", value: (r) => r.commissionRate, type: "percent" },
          { label: "Commission", value: (r) => r.commissionAmount, type: "currency" },
          { label: "Statut", value: (r) => COMMISSION_STATUS[r.status] || r.status },
        ],
      },
      {
        title: "Commissions par service",
        rows: summary.byService || [],
        columns: [
          { label: "Service", value: (r) => r.serviceName },
          { label: "Prestations", value: (r) => r.count, type: "number" },
          { label: "CA", value: (r) => r.revenue, type: "currency" },
          { label: "Commission", value: (r) => r.commission, type: "currency" },
          { label: "Taux moyen", value: (r) => r.averageRate, type: "percent" },
        ],
      },
    ],
  };
};

// --------------------------------------------------------------------------
// Rapport des cartes cadeaux
// --------------------------------------------------------------------------
export const buildGiftcardsExport = async (filters, activeTab) => {
  const [summaryRes, listRes, usageRes] = await Promise.all([
    giftcardReportService.getSummary(filters),
    giftcardReportService.getList(listParams(filters, { status: filters.status })),
    giftcardReportService.getUsage(listParams(filters)),
  ]);
  const summary = summaryRes?.data || {};
  const overview = summary.overview || {};
  const byStatus = Object.entries(summary.byStatus || {}).map(([status, data]) => ({
    status,
    ...data,
  }));

  return {
    title: "Rapport des cartes cadeaux",
    fileBaseName: "rapport-cartes-cadeaux",
    filtersInfo: [describePeriod(filters, summary.period)],
    summary: [
      { label: "Total cartes", value: overview.totalCards, type: "number" },
      { label: "Valeur totale", value: overview.totalValue, type: "currency" },
      { label: "Valeur utilisée", value: overview.usedValue, type: "currency" },
      { label: "Taux d'utilisation", value: overview.usageRate, type: "percent" },
    ],
    csvSection: activeTab === "usage" ? 1 : 0,
    sections: [
      {
        title: "Cartes cadeaux",
        rows: rowsOf(listRes),
        columns: [
          { label: "Code", value: (r) => r.code },
          { label: "Service", value: (r) => r.serviceName },
          { label: "Valeur unitaire", value: (r) => r.value, type: "currency" },
          { label: "Quantité", value: (r) => r.quantity, type: "number" },
          { label: "Valeur totale", value: (r) => r.totalValue, type: "currency" },
          { label: "Statut", value: (r) => translateStatus(r.status, "giftcard") },
          { label: "Date création", value: (r) => r.createdAt, type: "date" },
        ],
      },
      {
        title: "Utilisations",
        rows: rowsOf(usageRes),
        columns: [
          { label: "Carte", value: (r) => r.cardCode },
          { label: "Client", value: (r) => r.clientName },
          { label: "Service utilisé", value: (r) => r.serviceName },
          { label: "Rendez-vous", value: (r) => r.usedFor },
          { label: "Montant", value: (r) => r.value, type: "currency" },
          { label: "Date utilisation", value: (r) => r.usedAt, type: "date" },
          { label: "Employé", value: (r) => r.employeeName },
        ],
      },
      {
        title: "Répartition par statut",
        rows: byStatus,
        columns: [
          { label: "Statut", value: (r) => translateStatus(r.status, "giftcard") },
          { label: "Nombre", value: (r) => r.count, type: "number" },
          { label: "Valeur", value: (r) => r.value, type: "currency" },
          { label: "Part", value: (r) => r.percentage, type: "percent" },
        ],
      },
      {
        title: "Cartes par service",
        rows: summary.byService || [],
        columns: [
          { label: "Service", value: (r) => r.serviceName },
          { label: "Cartes", value: (r) => r.count, type: "number" },
          { label: "Valeur", value: (r) => r.value, type: "currency" },
          { label: "Utilisées", value: (r) => r.usedCount, type: "number" },
          { label: "Taux d'utilisation", value: (r) => r.usageRate, type: "percent" },
        ],
      },
    ],
  };
};

// --------------------------------------------------------------------------
// Rapport des abonnements
// --------------------------------------------------------------------------
export const buildSubscriptionsExport = async (filters, activeTab) => {
  const [summaryRes, listRes, usageRes] = await Promise.all([
    subscriptionReportService.getSummary(filters),
    subscriptionReportService.getList(
      listParams(filters, { status: filters.status, expiringSoon: filters.expiringSoon || undefined }),
    ),
    subscriptionReportService.getServiceUsage(getExportApiDateParams(filters)),
  ]);
  const summary = summaryRes?.data || {};
  const overview = summary.overview || {};
  const mrr = (summary.byPlan || []).reduce(
    (total, plan) => total + (plan.planPrice || 0) * (plan.activeCount || 0),
    0,
  );
  const renewalBase =
    (overview.activeSubscriptions || 0) + (overview.expiredSubscriptions || 0);
  const renewalRate = renewalBase
    ? ((overview.activeSubscriptions || 0) / renewalBase) * 100
    : 0;

  return {
    title: "Rapport des abonnements",
    fileBaseName: "rapport-abonnements",
    filtersInfo: [describePeriod(filters, summary.period)],
    summary: [
      { label: "Abonnements actifs", value: overview.activeSubscriptions, type: "number" },
      { label: "MRR", value: mrr, type: "currency" },
      { label: "Taux de renouvellement", value: renewalRate, type: "percent" },
      { label: "Valeur moyenne", value: overview.averageSubscriptionValue, type: "currency" },
      { label: "Expirés", value: overview.expiredSubscriptions, type: "number" },
      { label: "Annulés", value: overview.cancelledSubscriptions, type: "number" },
      { label: "CA total", value: overview.totalRevenue, type: "currency" },
    ],
    csvSection: activeTab === "usage" ? 1 : 0,
    sections: [
      {
        title: "Abonnements",
        rows: rowsOf(listRes),
        columns: [
          { label: "Code", value: (r) => r.code },
          { label: "Client", value: (r) => r.clientName },
          { label: "Email", value: (r) => r.clientEmail },
          { label: "Téléphone", value: (r) => r.clientPhone },
          { label: "Formule", value: (r) => r.planName },
          { label: "Montant", value: (r) => r.amount, type: "currency" },
          { label: "Début", value: (r) => r.startDate, type: "date" },
          { label: "Fin", value: (r) => r.endDate, type: "date" },
          { label: "Jours restants", value: (r) => r.daysLeft, type: "number" },
          { label: "Statut", value: (r) => translateStatus(r.status, "subscription") },
        ],
      },
      {
        title: "Utilisation des services",
        rows: rowsOf(usageRes),
        columns: [
          { label: "Service", value: (r) => r.serviceName },
          { label: "Disponibles", value: (r) => r.totalAvailable, type: "number" },
          { label: "Utilisés", value: (r) => r.totalUsed, type: "number" },
          { label: "Taux d'utilisation", value: (r) => r.usageRate, type: "percent" },
        ],
      },
      {
        title: "Répartition par formule",
        rows: summary.byPlan || [],
        columns: [
          { label: "Formule", value: (r) => r.planName },
          { label: "Prix", value: (r) => r.planPrice, type: "currency" },
          { label: "Souscriptions", value: (r) => r.count, type: "number" },
          { label: "Actives", value: (r) => r.activeCount, type: "number" },
          { label: "CA", value: (r) => r.revenue, type: "currency" },
          { label: "Part", value: (r) => r.percentage, type: "percent" },
        ],
      },
      {
        title: "Expirant bientôt",
        rows: summary.expiringSoon || [],
        columns: [
          { label: "Client", value: (r) => r.clientName },
          { label: "Formule", value: (r) => r.planName },
          { label: "Fin", value: (r) => r.endDate, type: "date" },
          { label: "Jours restants", value: (r) => r.daysLeft, type: "number" },
        ],
      },
    ],
  };
};
